import { Layout } from "@layouts";
import { Card } from "@molecules";
import { ActionBanner } from "@molecules/action-banner";
import { Icon, Image, ImageVariantType, Spinner, Button, CircleIcon } from "@atoms";
import { Partner } from "@api";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { BillingDetails, PagePaths, PartnerDashboardData, DashboardMetric, TeamEngagementData, LifestyleSummaryData } from "../../../types";
import { Users as UsersIcon, DollarSign as DollarSignIcon, UserCheck as UserCheckIcon, BarChart3 as BarChartIcon, TrendingUp, Target, ShieldCheck, Heart, Trophy, CheckCircle, Star } from "lucide-react";
import { BlogSection, PerksSection } from "@organisms";
import { AreaChartComponent, PieChartComponent, Card as ShadcnCard, CardContent as ShadcnCardContent, CardTitle as ShadcnCardTitle, CardDescription as ShadcnCardDescription, Badge as ShadcnBadge } from "@shadcn";
import { TransactionListItem } from "../lifestyle/main/transaction-list-item";
import dayjs from "dayjs";
const themeColors = require("@theme/colors") as Record<string, any>;

const colors = require("@theme/colors") as Record<string, any>;
const SECTION_MIN_HEIGHT = "24rem";
const LIFESTYLE_REWARDS_HEIGHT = `calc(${SECTION_MIN_HEIGHT} + 80px)`;
const SECTION_FIXED_DIMENSIONS = { minHeight: LIFESTYLE_REWARDS_HEIGHT, height: LIFESTYLE_REWARDS_HEIGHT };
const LIFESTYLE_SCROLL_CONTAINER_CLASSES = "flex-1 overflow-y-auto space-y-4 pr-1";
const REDUCED_SECTION_HEIGHT = `calc(${SECTION_MIN_HEIGHT} + 20px)`;
const BENEFITS_TEAM_SECTION_HEIGHT = `calc(${SECTION_MIN_HEIGHT} - 20px)`;
const MONTHLY_SAVINGS_BAR_COLOR = "#1B325F";
const MONTHLY_SAVINGS_BAR_SHADES = [themeColors.complimentary.darker, themeColors.complimentary.darker, themeColors.complimentary.darker];
const MONTHLY_SAVINGS_BAR_MAX_WIDTH = 60;
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const formatCurrency = (value: number) =>
  `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

type SectionHeaderProps = {
  icon: ReactNode;
  title: string;
  action?: ReactNode;
  className?: string;
};

const SectionHeader = ({
  icon,
  title,
  action,
  className = "",
}: SectionHeaderProps): JSX.Element => (
  <div className={`flex flex-row items-center justify-between gap-1 w-full ${className}`}>
    <div className="flex flex-row items-center">
      {icon ? <span className="mr-2 flex items-center">{icon}</span> : null}
      <div className="section-title">{title}</div>
    </div>
    {action ? <div className="flex-shrink-0">{action}</div> : null}
  </div>
);

export type HomePageTemplateProps = {
  todos: {
    variant: ImageVariantType;
    title: string;
    description: string;
    navigationString: string;
    index: number;
  }[];
  partner: Partner | null;
  navigateToPage: (index: number, to: string) => void;
  isSetupComplete: boolean | undefined;
  billingDetails: BillingDetails;
  partnerDashboardData: PartnerDashboardData | undefined;
  getHeroMetrics: () => DashboardMetric[];
  getLifestyleSummary: () => LifestyleSummaryData;
  isLoadingHeroMetrics: boolean;
  isLoadingLifestyleData: boolean;
  isLoadingBrandData: boolean;
  isLoadingSavingsData: boolean;
  isLoadingAirdropData: boolean;
  creditCardStatus: "valid" | "expired" | "expiringSoon";
  onApprovePendingTransaction: (transactionId: string) => Promise<void>;
  onRejectPendingTransaction: (transactionId: string) => Promise<void>;
  approvingTransactionIds: Set<string>;
  rejectingTransactionIds: Set<string>;
};

export const HomePageTemplate = ({
  todos,
  partner,
  navigateToPage,
  isSetupComplete,
  billingDetails,
  partnerDashboardData,
  getHeroMetrics,
  getLifestyleSummary,
  isLoadingHeroMetrics,
  isLoadingLifestyleData,
  isLoadingBrandData,
  isLoadingSavingsData,
  isLoadingAirdropData,
  creditCardStatus,
  onApprovePendingTransaction,
  onRejectPendingTransaction,
  approvingTransactionIds,
  rejectingTransactionIds,
}: HomePageTemplateProps): JSX.Element => {
  const { t } = useTranslation();
  const teamMembers = partner?.premiumSettings?.[0]?.users?.length ?? 0;
  const onboardedMembers =
    partner?.premiumSettings?.[0]?.users?.filter((user) => !!user.userId)
      .length ?? 0;
  const onboardedRate = teamMembers > 0 ? Math.round((onboardedMembers / teamMembers) * 100) : 0;
  const onboardedPercent = teamMembers > 0 ? Math.round((onboardedMembers / teamMembers) * 100) : 0;
  const onboardingProgress = Math.max(0, Math.min(100, onboardedPercent || 0));
  const currentLifestyleGroup = partner?.lsaGroups?.[0];
  const currentLifestyleGroupName = currentLifestyleGroup?.groupName;
  const currentLifestyleGroupResetDate = (currentLifestyleGroup as any)?.resetDate;
  const greetingName = partner?.firstName?.trim();
  const greetingTarget = greetingName || t("common.hiFallbackName");
  const isBillingComplete = Array.isArray(billingDetails?.creditCards) && billingDetails.creditCards.length > 0;
  const monthNames =
    (t("common.monthNamesShort", { returnObjects: true }) as string[]) || MONTH_NAMES;

  const placeholderAvatars = [
    { initials: "AD", color: "#000000" },
    { initials: "JS", color: "#444444" },
    { initials: "LM", color: "#777777" },
  ];
  const monthlySavingsRaw = partnerDashboardData?.dashboardData?.monthlySavings;
  const hasRewardsPerformanceResponse = Array.isArray(monthlySavingsRaw);
  const normalizedMonthlySavings = monthlySavingsRaw ?? [];
  const hasRewardsPerformanceData = normalizedMonthlySavings.length > 0;
  const sortedMonthlySavings = [...normalizedMonthlySavings].sort((a, b) => {
    if (a._id.year === b._id.year) {
      return a._id.month - b._id.month;
    }
    return a._id.year - b._id.year;
  });
  const monthlySavingsMap = new Map<string, number>();
  sortedMonthlySavings.forEach((entry) => {
    const mapKey = `${entry._id.year}-${entry._id.month}`;
    monthlySavingsMap.set(mapKey, entry.savings / 100);
  });
  const latestEntry = sortedMonthlySavings[sortedMonthlySavings.length - 1];
  const baseDate = latestEntry
    ? new Date(latestEntry._id.year, latestEntry._id.month - 1, 1)
    : new Date();
  const monthlySavingsChartData = Array.from({ length: 6 }).map((_, index) => {
    const date = new Date(baseDate.getFullYear(), baseDate.getMonth() - (5 - index), 1);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const mapKey = `${year}-${month}`;
    const translatedMonth =
      monthNames?.[month - 1] ?? MONTH_NAMES[month - 1] ?? `Month ${month}`;
    return {
      name: translatedMonth,
      value: monthlySavingsMap.get(mapKey) ?? 0,
    };
  });
  const monthlySavingsChartDataWithColors = monthlySavingsChartData.map((entry, index) => ({
    ...entry,
    color: MONTHLY_SAVINGS_BAR_SHADES[index % MONTHLY_SAVINGS_BAR_SHADES.length],
  }));
  const totalAnnualSavings = normalizedMonthlySavings.reduce((sum, entry) => sum + entry.savings, 0) / 100;
  const latestMonthlySavings =
    sortedMonthlySavings.length > 0
      ? sortedMonthlySavings[sortedMonthlySavings.length - 1].savings / 100
      : 0;
  const topBrands = partnerDashboardData?.dashboardData?.topBrands ?? [];
  const maxBrandPurchases = topBrands.reduce((max, brand) => {
    const value = typeof brand.totalPurchases === "number" ? brand.totalPurchases : 0;
    return value > max ? value : max;
  }, 0);
  const longestBrandNameLength = topBrands.reduce((max, brand) => {
    const length = brand.brandName ? brand.brandName.length : 0;
    return length > max ? length : max;
  }, 0);
  const nameColumnWidth =
    longestBrandNameLength > 0 ? `${longestBrandNameLength + 2}ch` : "140px";
  const getBrandBarWidth = (purchases?: number | null): string => {
    if (!maxBrandPurchases || typeof purchases !== "number" || purchases <= 0) {
      return "0%";
    }
    const pct = (purchases / maxBrandPurchases) * 100;
    return `${Math.max(pct, 6)}%`;
  };
  const boostAmount = partner?.offerAmountsManagement?.offerBoost?.boostAmount ?? 0;
  const boostActivated =
    partner?.offerAmountsManagement?.offerBoost?.isBoostingAllOffers ?? boostAmount > 0;
  const boostLabel = boostActivated ? t("rewards.boostOn") : t("rewards.boostOff");
  const boostLabelColor = boostActivated ? themeColors.successful.dark : themeColors.secondary.dark;
  const boostLabelStyle = { color: boostLabelColor };
  const savingsHighlightMetrics = [
    {
      label: t("rewards.annualSavings"),
      value: formatCurrency(totalAnnualSavings),
    },
    {
      label: t("rewards.monthlySavings"),
      value: formatCurrency(latestMonthlySavings),
    },
    {
      label: boostLabel,
      value: `${boostAmount}%`,
      labelStyle: boostLabelStyle,
    },
  ];

  const renderLifestyleSection = (): JSX.Element => {
    const header = (
      <SectionHeader
        icon={<Heart size={18} className="text-black" />}
        title={t("lifestyle.title")}
        action={
          <Button
            variant="link"
            label={t("common.manage")}
            onClick={() => navigateToPage(3, "/dashboard/lifestyle/main")}
          />
        }
      />
    );

    if (isLoadingLifestyleData) {
      return (
        <ShadcnCard
          className="flex flex-col p-6 h-full"
          style={SECTION_FIXED_DIMENSIONS}
        >
          {header}
          <div className={LIFESTYLE_SCROLL_CONTAINER_CLASSES}>
            <div className="grid grid-cols-3 gap-3">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="text-center p-5 bg-gray-100 rounded-lg animate-pulse">
                  <div className="h-6 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-16 mx-auto"></div>
                </div>
              ))}
            </div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
              {Array.from({ length: 2 }).map((_, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-gray-100 rounded animate-pulse">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                    <div className="space-y-1">
                      <div className="h-4 bg-gray-200 rounded w-20"></div>
                      <div className="h-3 bg-gray-200 rounded w-16"></div>
                    </div>
                  </div>
                  <div className="h-4 bg-gray-200 rounded w-12"></div>
                </div>
              ))}
            </div>
          </div>
        </ShadcnCard>
      );
    }

    const lifestyleData = getLifestyleSummary();

    if (!lifestyleData) {
      if (!teamMembers) {
        return (
          <ShadcnCard
            className="flex flex-col p-10 h-full"
            style={SECTION_FIXED_DIMENSIONS}
          >
            {header}
            <div className="flex-1 overflow-y-auto">
              <div className="flex flex-col items-center justify-center text-center gap-4 pt-12">
                <div className="flex -space-x-3">
              {placeholderAvatars.map((avatar) => (
                <div
                  key={`placeholder-avatar-${avatar.initials}`}
                  className="h-12 w-12 rounded-full border-2 border-white flex items-center justify-center text-sm font-semibold text-neutral-lightest shadow-sm"
                  style={{ backgroundColor: avatar.color }}
                    >
                      {avatar.initials}
                    </div>
                  ))}
                </div>
                <div className="space-y-1">
                  <p className="text-base font-semibold text-neutral-darkest">
                    {t("lifestyle.noTeamMembersTitle")}
                  </p>
                  <p className="metric-subtitle max-w-xs">
                    {t("lifestyle.noTeamMembersSubtitle")}
                  </p>
                </div>
                <div>
                  <Button
                    onClick={() => navigateToPage(1, "/dashboard/team")}
                    variant="secondary"
                    label={t("lifestyle.inviteMembers")}
                    autosize
                    color="#FF7A00"
                    hoverColor="#E56B00"
                    textColor="#FFFFFF"
                  />
                </div>
              </div>
            </div>
          </ShadcnCard>
        );
      }

      return (
        <ShadcnCard
          className="flex flex-col p-14"
          style={{ minHeight: SECTION_MIN_HEIGHT }}
        >
          {header}
          <div className="flex flex-col items-center justify-center text-center gap-4 pt-12">
            <div className="space-y-1">
              <p className="text-base font-semibold text-neutral-darkest">
                {t("lifestyle.noGroupTitle")}
              </p>
              <p className="metric-subtitle max-w-xs">
                {t("lifestyle.noGroupSubtitle")}
              </p>
            </div>
            <div>
              <Button
                onClick={() => navigateToPage(3, "/dashboard/lifestyle/main")}
                variant="secondary"
                label={t("lifestyle.createGroup")}
                autosize
                color="#FF7A00"
                hoverColor="#E56B00"
                textColor="#FFFFFF"
              />
            </div>
          </div>
        </ShadcnCard>
      );
    }
    if (!lifestyleData.hasLifestyleFund) {
      return (
        <ShadcnCard
          className="flex flex-col p-8 h-full"
          style={SECTION_FIXED_DIMENSIONS}
        >
          {header}
          <div className="flex flex-1 flex-col overflow-y-auto">
            <div className="flex flex-1 flex-col items-center justify-center text-center text-gray-500">
              <Heart className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="text-sm font-medium">{t("lifestyle.noBenefitsTitle")}</p>
              <p className="text-xs text-gray-500 mt-1">
                {t("lifestyle.noBenefitsSubtitle")}
              </p>
              <div className="mt-3 flex flex-col items-center gap-2">
                <Button
                  onClick={() => navigateToPage(1, "/dashboard/lifestyle/create-group")}
                  variant="primary"
                  label={t("lifestyle.createBenefits")}
                  color="brand-orange"
                  hoverColor="primary-dark"
                  size="sm"
                />
              </div>
            </div>
          </div>
        </ShadcnCard>
      );
    }

    const pendingTransactionsPreview = lifestyleData.pendingTransactions.slice(0, 2);

    return (
      <ShadcnCard
          className="flex flex-col p-6 h-full"
          style={SECTION_FIXED_DIMENSIONS}
        >
          {header}
          <div className={LIFESTYLE_SCROLL_CONTAINER_CLASSES}>
            <div className="grid grid-cols-3 gap-3 pb-2">
              <Card
                variant="metric"
                label={t("lifestyle.totalRequested")}
                value={formatCurrency(lifestyleData.totalReimbursed)}
                valueClassName="font-bold text-neutral-darkest"
              />
              <Card
                variant="metric"
                label={t("lifestyle.employeeParticipation")}
                value={`${Math.floor(lifestyleData.participationRate || 0)}%`}
                valueClassName="font-bold text-neutral-darkest"
              />
              <Card
                variant="metric"
                label={t("lifestyle.avgPerUser")}
                value={formatCurrency(lifestyleData.avgPerUser)}
                valueClassName="font-bold text-neutral-darkest"
              />
            </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="small-title">{t("lifestyle.recentPending")}</span>
              <button
                onClick={() => navigateToPage(1, "/dashboard/lifestyle/transactions")}
                className="text-sm font-normal text-link-neutral"
              >
                {t("common.viewAll")}
              </button>
            </div>
            <div className="space-y-3">
              {pendingTransactionsPreview.length === 0 ? (
                <ShadcnCard className="border border-dashed border-neutral-light/70 bg-neutral-lightest/50 py-0 shadow-none">
                  <ShadcnCardContent className="px-5 py-6 text-center text-sm text-muted-foreground">
                    {t("lifestyle.noPending")}
                  </ShadcnCardContent>
                </ShadcnCard>
              ) : (
                pendingTransactionsPreview.map((transaction, index) => {
                  const {
                    firstName,
                    lastName,
                    transactionId,
                    date,
                    amount,
                    categoryName,
                    companyName,
                  } = transaction;
                  const fullName =
                    [firstName, lastName].filter(Boolean).join(" ") ||
                    t("lifestyle.pendingUser");
                  const transactionKey =
                    date?.toString?.() ?? `${fullName}-${amount ?? index}`;
                  return (
                    <ShadcnCard
                      key={transactionKey}
                      className="border-none bg-white/95 py-0 shadow-none"
                    >
                      <ShadcnCardContent className="px-0 py-0">
                        <TransactionListItem
                          memberName={fullName}
                          companyName={companyName || categoryName || ""}
                          secondaryLabel={undefined}
                          submittedDate={date}
                          amount={amount}
                          onApprove={
                            transactionId ? () => onApprovePendingTransaction(transactionId) : undefined
                          }
                          onReject={
                            transactionId ? () => onRejectPendingTransaction(transactionId) : undefined
                          }
                          isApproving={transactionId ? approvingTransactionIds.has(transactionId) : false}
                          isRejecting={transactionId ? rejectingTransactionIds.has(transactionId) : false}
                          disableApprove={creditCardStatus === "expired"}
                          className="p-4 bg-gray-50 rounded-lg"
                        />
                      </ShadcnCardContent>
                    </ShadcnCard>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </ShadcnCard>
    );
  };

  return (
    <Layout
      variant="dashboard"
      header={t("common.dashboard")}
      subheader={
        <div className="page-subtitle mt-2">
          {t("common.hiWithName", { name: greetingTarget })}
        </div>
      }
    >
      <div>
      {creditCardStatus === "expired" ? (
        <div className="pt-4">
          <ActionBanner
            text="🚨 Your credit card has expired, please update your info."
            onClick={() => navigateToPage(3, "/dashboard/billing")}
            className="max-w-5xl"
          />
        </div>
      ) : <div className="pb-4"></div>}
      {isSetupComplete === undefined ?
        <div></div>
      : isSetupComplete === false /* || true */ ?
      <div>
        <div className="mt-8 mb-4">
          <h2 className="color-black text-xl">{t("setup.finishSetup")}</h2>
        </div>
        <div className="flex flex-wrap my-3">
          {todos.map(({ variant, titleKey, descriptionKey, navigationString, index }, idx) => {
            const isPaymentTodo = variant === "paymentInfo";
            const iconVariant: ImageVariantType = isPaymentTodo && isBillingComplete ? "paymentInfoComplete" : variant;
            const isSectionIncomplete = isPaymentTodo ? !isBillingComplete : true;
            const completionLabel = isPaymentTodo && isBillingComplete ? t("common.complete") : undefined;

            return (
              <div key={index} className="mr-5 mb-5 max-w-min">
                <Card
                  onClick={() => navigateToPage(index, "/dashboard/" + navigationString)}
                  variant="onboard"
                  icon={<Image variant={iconVariant} width={204} height={152} />}
                  title={t(titleKey)}
                  description={t(descriptionKey)}
                  index={idx}
                  isSectionIncomplete={isSectionIncomplete}
                  completionLabel={completionLabel}
                />
              </div>
            );
          })}
        </div>
      </div>
      :
        <div className="mr-4 hide-scrollbar flex flex-col overflow-x-hidden" style={{ gap: "18px" }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 min-w-0" style={{ gap: "18px" }}>
            <div className="flex flex-col">
              <div
                className="rounded-xl border border-neutral-light bg-white px-5 py-6 overflow-y-auto"
                style={{ height: BENEFITS_TEAM_SECTION_HEIGHT }}
              >
                <SectionHeader
                  icon={<ShieldCheck size={18} className="text-black" />}
                  title={t("benefits.title")}
                  action={
                    <Button
                      variant="link"
                      label={t("common.manage")}
                      onClick={() => navigateToPage(1, "/dashboard/benefits")}
                    />
                  }
                />
                <PerksSection
                  navigateToPage={navigateToPage}
                  addPlusIcon={true}
                  currentLifestyleGroupName={currentLifestyleGroupName}
                  currentLifestyleGroupResetDate={currentLifestyleGroupResetDate}
                />
              </div>
            </div>

            {/* Team Overview */}
            <div className="flex flex-col lg:mt-0">
              <div
                className="rounded-xl border border-neutral-light bg-white p-6 overflow-y-auto hide-scrollbar"
                style={{ height: BENEFITS_TEAM_SECTION_HEIGHT }}
              >
                <SectionHeader
                  icon={<UsersIcon size={20} />}
                  title={t("team.title")}
                  className="pb-4"
                  action={
                    <Button
                      variant="link"
                      label={t("common.manage")}
                      onClick={() => navigateToPage(1, "/dashboard/team")}
                    />
                  }
                />
                {teamMembers > 0 ? (
                  <div className="flex flex-col gap-5 pt-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <Card
                        variant="metric"
                        label={t("team.teamMembers")}
                        value={teamMembers.toLocaleString()}
                      />
                      <Card
                        variant="metric"
                        label={t("team.onboardedMembers")}
                        value={onboardedMembers.toLocaleString()}
                      />
                      <Card
                        variant="metric"
                        label={t("team.onboardedRate")}
                        value={`${onboardedRate}%`}
                      />
                    </div>
                    <div className="flex-1 flex flex-col justify-center gap-4">
                      <div className="flex items-center justify-between">
                        <div className="small-title">{t("team.onboardingProgress")}</div>
                      </div>
                      <div className="w-full">
                        <div className="h-4 w-full rounded-full bg-neutral-lightest border border-neutral-light overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${onboardingProgress}%`,
                              backgroundColor: themeColors.primary.medium,
                              transition: "width 300ms ease",
                            }}
                          />
                        </div>
                        <div className="flex justify-between text-xs text-neutral-mediumDark mt-2">
                          <span>
                            {t("team.onboardedSummary", {
                              onboarded: onboardedMembers,
                              total: teamMembers,
                            })}
                          </span>
                        </div>
                      </div>
                      {onboardingProgress === 100 && (
                        <div className="flex items-center justify-start text-sm font-semibold text-successful-medium">
                          <CheckCircle className="h-4 w-4 mr-2" />
                          <span className="text-black">{t("team.allOnboarded")}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center text-center py-8 text-gray-500">
                    <UsersIcon className="h-10 w-10 mb-3 text-gray-300" />
                    <p className="text-sm font-medium">{t("team.noMembersTitle")}</p>
                    <p className="text-xs text-gray-500 mt-1 max-w-sm">
                      {t("team.noMembersSubtitle")}
                    </p>
                    <button
                      type="button"
                      onClick={() => navigateToPage(1, "/dashboard/team")}
                      className="mt-4 px-4 py-2 text-sm font-semibold text-white rounded-full bg-brand-orange hover:bg-primary-dark transition-colors"
                    >
                      {t("team.inviteMembers")}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* MAIN DASHBOARD GRID */}
          <div className="grid grid-cols-1 lg:grid-cols-2 min-w-0" style={{ gap: "18px" }}>
            {/* LIFESTYLE BENEFITS OVERVIEW */}
            <div className="flex flex-col h-full" style={SECTION_FIXED_DIMENSIONS}>
              {renderLifestyleSection()}
            </div>

            {/* Rewards Performance */}
            <div className="flex flex-col h-full" style={SECTION_FIXED_DIMENSIONS}>
              {isLoadingSavingsData ? (
                <ShadcnCard
                  className="flex flex-col gap-6 px-6 h-full"
                  style={SECTION_FIXED_DIMENSIONS}
                >
                  <SectionHeader icon={<Trophy size={20} />} title={t("rewards.title")} />
                  <div className="flex items-center justify-center h-48">
                    <Spinner variant="large" />
                  </div>
                </ShadcnCard>
              ) : hasRewardsPerformanceData ? (
                <AreaChartComponent
                  data={monthlySavingsChartDataWithColors}
                  title={t("rewards.monthlyTrend")}
                  height="100%"
                  xAxisLabel={t("rewards.xAxis")}
                  yAxisLabel={t("rewards.yAxis")}
                  color={MONTHLY_SAVINGS_BAR_COLOR}
                  tooltipLabel={t("rewards.tooltipLabel")}
                  tooltipPrefix="$"
                  cardClassName="flex flex-col h-full min-h-0 overflow-hidden"
                  cardStyle={SECTION_FIXED_DIMENSIONS}
                  contentClassName="flex flex-col gap-4 h-full min-h-0 overflow-hidden"
                  chartContainerClassName="flex-1 w-full min-w-0 min-h-[220px] max-h-[280px] overflow-hidden"
                  yAxisLabelProps={{ style: { fontSize: 11 }, dy: 22 }}
                  hideHeader
                >
                  <>
                    <div className="flex flex-col gap-3">
                      <SectionHeader icon={<Trophy size={20} />} title={t("rewards.title")} className="w-full shrink-0 pb-3" />
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pb-10">
                        {savingsHighlightMetrics.map((metric) => (
                          <Card
                            key={metric.label}
                            variant="metric"
                            label={metric.label}
                            value={metric.value}
                            labelStyle={metric.labelStyle}
                          />
                        ))}
                      </div>
                    </div>
                  </>
                </AreaChartComponent>
              ) : hasRewardsPerformanceResponse ? (
                <ShadcnCard
                  className="flex flex-col gap-6 px-6 h-full"
                  style={SECTION_FIXED_DIMENSIONS}
                >
                  <SectionHeader icon={<Trophy size={20} />} title={t("rewards.title")} />
                  <div className="flex flex-1 items-center justify-center">
                    <div className="flex flex-1 flex-col items-center justify-center text-center text-gray-500">
                      {/* <BarChartIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" /> */}
                      <Trophy className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p className="text-sm font-medium">{t("rewards.noData")}</p>
                      <p className="text-xs mt-1">{t("rewards.noDataSubtitle")}</p>
                    </div>
                  </div>
                </ShadcnCard>
              ) : (
                <ShadcnCard
                  className="flex flex-col gap-6 px-6 h-full"
                  style={SECTION_FIXED_DIMENSIONS}
                >
                  <SectionHeader icon={<Trophy size={20} />} title={t("rewards.title")} />
                  <div className="flex items-center justify-center h-48">
                    <Spinner variant="large" />
                  </div>
                </ShadcnCard>
              )}
            </div>

            {/* RECENT AIRDROPS */}
            {(() => {
              return (
                <div className="flex flex-col" style={{ minHeight: SECTION_MIN_HEIGHT }}>
                  <ShadcnCard
                    className="flex flex-col p-5"
                    style={{ minHeight: SECTION_MIN_HEIGHT }}
                  >
                    <SectionHeader
                      icon={<Icon variant="gift" size={20} />}
                      title={t("airdrops.title")}
                      action={
                        partnerDashboardData?.dashboardData?.lastThreeAirdrops &&
                        partnerDashboardData.dashboardData.lastThreeAirdrops.length > 0 ? (
                          <Button
                            variant="link"
                            label={t("airdrops.viewAll")}
                            onClick={() => navigateToPage(3, "/dashboard/airdrop/main")}
                          />
                        ) : null
                      }
                    />
                    <div className="h-full flex flex-col">
                      {isLoadingAirdropData ? (
                        <div className="flex-1 flex items-center justify-center">
                          <Spinner variant="large" />
                        </div>
                      ) : partnerDashboardData?.dashboardData?.lastThreeAirdrops && partnerDashboardData?.dashboardData?.lastThreeAirdrops?.length > 0 ? (
                        <>
                          <div className="flex-1 space-y-3 overflow-y-auto hide-scrollbar">
                            {partnerDashboardData?.dashboardData?.lastThreeAirdrops?.slice(0, 3).map((airdrop, index) => (
                              <div key={`${airdrop.firstName}-${airdrop.lastName}-${airdrop.date}-${index}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-center gap-3">
                                  <Icon variant="gift" size={16} color="#4B5563" />
                                  <div>
                                    <p className="text-sm font-medium">{`${airdrop.firstName} ${airdrop.lastName}`.trim()}</p>
                                    <div className="flex flex-row items-center gap-2">
                                      <p className="text-xs text-gray-600">{airdrop.brandName}</p>
                                      <p className="text-xs text-gray-500">{dayjs(airdrop.date).format("MMM D, YYYY")}</p>
                                    </div>
                                    
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm font-medium">${airdrop.amount}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      ) : (
                        <div className="flex flex-1 items-center justify-center">
                          <div className="flex flex-1 flex-col items-center justify-center text-center text-gray-500">
                            <div className="mx-auto mb-3">
                              <Icon variant="gift" size={48} color="#D1D5DB" />
                            </div>
                            <p className="text-sm font-medium">{t("airdrops.none")}</p>
                            <p className="text-xs mt-1">
                              <span 
                                onClick={() => navigateToPage(1, "/dashboard/airdrop/main")}
                                className="text-primary-medium hover:text-primary-dark underline cursor-pointer"
                              >
                                {t("airdrops.noneCta")}
                              </span>
                              {" "}{t("airdrops.noneSubtitle")}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </ShadcnCard>
                </div>
              );
            })()}

            {/* POPULAR BRANDS */}
            <div className="flex flex-col" style={{ minHeight: SECTION_MIN_HEIGHT }}>
              <ShadcnCard
                className="flex flex-col p-5"
                style={{ minHeight: SECTION_MIN_HEIGHT }}
              >
                <SectionHeader icon={<Star size={18} />} title={t("brands.title")} />
                <div className="space-y-4 h-full flex flex-col">
                  {isLoadingBrandData ? (
                    <div className="flex-1 space-y-3">
                      {Array.from({ length: 4 }).map((_, index) => (
                        <div key={index} className="flex items-center justify-between animate-pulse">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                            <div className="h-4 bg-gray-200 rounded w-24"></div>
                          </div>
                          <div className="h-4 bg-gray-200 rounded w-16"></div>
                        </div>
                      ))}
                    </div>
                  ) : topBrands.length > 0 ? (
                    <div className="flex-1 space-y-3">
                      {topBrands.map((brand, index) => (
                        <div
                          key={index}
                          className="grid items-center gap-2"
                          style={{ gridTemplateColumns: `${nameColumnWidth} 1fr 120px` }}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden">
                              {brand.brandLogo ? (
                                <img 
                                  src={brand.brandLogo} 
                                  alt={brand.brandName}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                                  <span className="text-gray-600 font-bold text-sm">
                                    {brand.brandName?.charAt(0)?.toUpperCase()}
                                  </span>
                                </div>
                              )}
                            </div>
                            <span className="font-medium text-sm">{brand.brandName}</span>
                          </div>
                          <div className="flex items-center gap-3 w-full">
                            <div className="w-full h-2 bg-neutral-light rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all"
                                style={{ width: getBrandBarWidth(brand.totalPurchases), backgroundColor: "#1B325F" }}
                              />
                            </div>
                          </div>
                          <span className="text-sm text-gray-600 whitespace-nowrap text-right w-full">
                            {brand.totalPurchases?.toLocaleString()}{" "}
                            {brand.totalPurchases === 1 ? t("brands.purchase") : t("brands.purchases")}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-1 items-center justify-center">
                      <div className="flex flex-1 flex-col items-center justify-center text-center text-gray-500">
                        <Star className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                        <p className="text-sm font-medium">{t("brands.noData")}</p>
                        <p className="text-xs mt-1">{t("brands.noDataSubtitle")}</p>
                      </div>
                    </div>
                  )}
                </div>
              </ShadcnCard>
            </div>

          </div>

          {/* LEARN MORE */}
          <div>
            <BlogSection page={PagePaths.home} />
          </div>
        </div>
      }
      </div>
    </Layout>
  );
};
